import React from 'react';
import { Network, Library, Microscope, Globe, type LucideIcon } from 'lucide-react';
import type { MessageSourcesUsed, SourceFamilyUsage } from '../../apiService';
import { formatCount } from '../../utils/plural';

/**
 * Per-message source icons.
 *
 * Shows which data-source families informed an assistant answer:
 *   • Regulatory graph (hero) — the Neo4j structured law
 *   • Document library        — the EBA/SFSA publication PDFs
 *   • Deep research           — delegated multi-agent investigation
 *   • Online search           — live web search beyond the curated corpus
 *
 * Filled + colored = the source actually contributed; greyed outline =
 * available but not used. The full set always renders (when attribution
 * exists) so "the graph was NOT used here" is legible at a glance. Counts
 * surface on hover. Colors reference DESIGN.md tokens via CSS custom
 * properties (no raw hex). See backend source_attribution.py for the data.
 */

type Family = 'graph' | 'documents' | 'research' | 'online_search';

interface FamilySpec {
    key: Family;
    label: string;
    description: string;  // what this source is — shown in the hover tooltip
    Icon: LucideIcon;
    color: string;        // token-mapped CSS custom property
    bg: string;           // light tint when used
    border: string;       // border tint when used
    hero?: boolean;       // graph is the hero — rendered slightly larger
}

// graph → teal (the sanctioned graph color), documents → citation blue,
// research → cyan ("logic / synthesis" node token), online search → copper
// (warm "open web / outside the curated corpus" accent).
const FAMILIES: FamilySpec[] = [
    {
        key: 'graph',
        label: 'Regulatory graph',
        description: 'The structured EU legislation — CRR/CRD articles, definitions, cross-references and calculations.',
        Icon: Network,
        color: 'var(--secondary)',
        bg: 'color-mix(in srgb, var(--secondary) 12%, transparent)',
        border: 'color-mix(in srgb, var(--secondary) 35%, transparent)',
        hero: true,
    },
    {
        key: 'documents',
        label: 'Document library',
        description: 'The publication library — EBA & SFSA guidelines, technical standards and Q&As.',
        Icon: Library,
        color: 'var(--link)',
        bg: 'var(--link-bg)',
        border: 'color-mix(in srgb, var(--link) 30%, transparent)',
    },
    {
        key: 'research',
        label: 'Deep research',
        description: 'A delegated multi-agent deep-dive that investigates across the graph and documents.',
        Icon: Microscope,
        color: 'var(--node-reasoning)',
        bg: 'color-mix(in srgb, var(--node-reasoning) 12%, transparent)',
        border: 'color-mix(in srgb, var(--node-reasoning) 35%, transparent)',
    },
    {
        key: 'online_search',
        label: 'Online search',
        description: 'Live web search for sources beyond the curated corpus — official sites, guidance and news.',
        Icon: Globe,
        color: 'var(--accent-copper)',
        bg: 'color-mix(in srgb, var(--accent-copper) 12%, transparent)',
        border: 'color-mix(in srgb, var(--accent-copper) 35%, transparent)',
    },
];

interface MessageSourceIconsProps {
    sources?: MessageSourcesUsed;
}

export default function MessageSourceIcons({ sources }: MessageSourceIconsProps) {
    // The row is ALWAYS shown on an assistant answer. Each family renders
    // filled when it contributed and greyed when it didn't — including when
    // nothing was used (e.g. a conversational reply) or when no attribution was
    // recorded (legacy messages), in which case every icon is greyed.
    return (
        // Inline group — the parent row positions this beside the feedback
        // buttons and handles top margin / left alignment.
        <div
            className="flex items-center gap-1.5"
            aria-label="Sources used for this answer"
        >
            {FAMILIES.map(({ key, label, description, Icon, color, bg, border, hero }) => {
                const usage: SourceFamilyUsage = sources?.[key] ?? { used: false, count: 0 };
                const used = usage.used;
                const chipSize = hero ? 'h-7 w-7' : 'h-6 w-6';
                const iconSize = hero ? 'h-4 w-4' : 'h-3.5 w-3.5';
                const stateText = used
                    ? `Used · ${formatCount(usage.count, { one: 'query', other: 'queries' })}`
                    : 'Not used in this answer';
                return (
                    <div key={key} className="group relative">
                        <span
                            aria-label={`${label}: ${stateText}`}
                            className={`inline-flex ${chipSize} items-center justify-center rounded-lg border transition-colors ${used ? '' : 'opacity-40'}`}
                            style={
                                used
                                    ? { color, backgroundColor: bg, borderColor: border }
                                    : {
                                        color: 'var(--fg-tertiary)',
                                        backgroundColor: 'transparent',
                                        borderColor: 'var(--border)',
                                    }
                            }
                        >
                            <Icon className={iconSize} strokeWidth={2} />
                        </span>
                        {/* Hover tooltip: what the source is + whether it was used */}
                        <div
                            role="tooltip"
                            className="pointer-events-none absolute bottom-full left-0 z-50 mb-2 w-56 rounded-md bg-primary px-3 py-2 text-left opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100"
                        >
                            <div className="text-xs font-semibold text-on-primary">{label}</div>
                            <div className="mt-0.5 text-[11px] leading-snug text-on-primary opacity-80">
                                {description}
                            </div>
                            <div
                                className="mt-1 text-[11px] font-medium"
                                style={{ color: used ? color : 'var(--on-primary)' }}
                            >
                                {stateText}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
